import React, { useEffect, useState, lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "./components/common/ScrollToTop";
import Layout from "./components/layout";
import ProtectedRoute from "./components/common/ProtectedRoute";
import AuthRoute from "./components/common/AuthRoute";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "./utils/store";
import { setAuthToken, setCareNavigatorId, setIsCareNavigator, setIsProfileComplete, setPatientLoginData } from "./utils/store/auth/AuthSlice";
import { getCareNavigatorPatientApi } from "./utils/api/services";
import { toast } from "react-toastify";
import Loader from "./components/common/Loader";
import ManualVitalEntry from "./pages/AuthPages/ManualVitalEntry";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";

const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const LoginOtp = lazy(() => import("./pages/LoginOtp"));
const SsoLogin = lazy(() => import("./pages/SsoLogin"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const Dashboard = lazy(() => import("./pages/AuthPages/Dashboard"));
const CareTeam = lazy(() => import("./pages/AuthPages/CareTeam"));
const CreateCareTeam = lazy(() => import("./pages/AuthPages/CreateCareTeam"));
const Profile = lazy(() => import("./pages/AuthPages/Profile"));
const MyAppointment = lazy(() => import("./pages/AuthPages/MyAppointment"));
const SavedDocument = lazy(() => import("./pages/AuthPages/SavedDocument"));
const SavedDocumentView = lazy(() => import("./pages/AuthPages/SavedDocumentView"));
const BookAppointment = lazy(() => import("./pages/AuthPages/BookAppointment"));
const BookAppointmentThanks = lazy(() => import("./pages/AuthPages/BookAppointmentThanks"));
const TalkToDoctor = lazy(() => import("./pages/AuthPages/TalkToDoctor"));
const Help = lazy(() => import("./pages/AuthPages/Help"));
const Assessments = lazy(() => import("./pages/AuthPages/Assessments"));
const AssessmentTest = lazy(() => import("./pages/AuthPages/AssessmentTest"));
const MedicalRecord = lazy(() => import("./pages/AuthPages/MedicalRecord"));
const MyMedicines = lazy(() => import("./pages/AuthPages/MyMedicines"));
const AppointmentDetails = lazy(() => import("./pages/AuthPages/AppointmentDetails"));
const RpmAlertManagement = lazy(() => import("./pages/AuthPages/RpmAlertManagement"));
const RPMAlertManagementDetails = lazy(() => import("./pages/AuthPages/RPMAlertManagementDetails"));
const BehaviouralHealth = lazy(() => import("./pages/AuthPages/BehaviouralHealth"));
const BehaviouralHealthDetails = lazy(() => import("./pages/AuthPages/BehaviouralHealthDetails"));
const LabTests = lazy(() => import("./pages/AuthPages/LabTests"));
const CallNow = lazy(() => import("./pages/AuthPages/CallNow"));
const BookLabtest = lazy(() => import("./pages/AuthPages/BookLabtest"));
const LabtestCart = lazy(() => import("./pages/AuthPages/LabtestCart"));
const LabTestDetails = lazy(() => import("./pages/AuthPages/LabTestDetails"));
const CarePlan = lazy(() => import("./pages/AuthPages/CarePlan"));
const LabCartSummary = lazy(() => import("./pages/AuthPages/LabCartSummary"));
const BookedLabDetails = lazy(() => import("./pages/AuthPages/BookedLabDetails"));
const Chat = lazy(() => import("./pages/AuthPages/Chat"));
const AssessmentCheckResult = lazy(() => import("./pages/AuthPages/AssessmentCheckResult"));
const Setting = lazy(() => import("./pages/AuthPages/Setting"));
const HraResult = lazy(() => import("./pages/AuthPages/HraResult"));
const OmronDevice = lazy(() => import("./pages/AuthPages/OmronDevice"));
const PricingPolicy = lazy(() => import("./pages/PricingPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

const App = () => {
  const [loading, setLoading] = useState(false)
  const token = useSelector((state: RootState) => state.auth.token);
  const { isCareNavigator } = useSelector((state: RootState) => state.auth)
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate();

  const fetchPatientLogin = async () => {
    try {
      setLoading(true)
      const result = await getCareNavigatorPatientApi()
      if (result?.success) {
        setLoading(false)
        if (result?.userDetails) {
          dispatch(setPatientLoginData(result?.userDetails && result?.userDetails[0]))
          localStorage.setItem('patientInfo', JSON.stringify(result?.userDetails[0]))
          localStorage.setItem('isProfileCompleted', JSON.stringify(result?.userDetails[0]?.isProfileCompleted === 1 ? true : false))
          dispatch(setIsProfileComplete(result?.userDetails[0]?.isProfileCompleted === 1 ? true : false))
          if (result?.userDetails[0]?.isFirstLogin === 0 || result?.userDetails[0]?.isProfileCompleted === 0) {
            navigate('/myaccount/profile')
          }
          else {
            navigate('/dashboard')
          }
        }
      }
      else {
        setLoading(false)
        toast.error(result?.message)
      }
    } catch (error: any) {
      toast.error(error?.data?.errors || error?.message);
    }
  }
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tokenFromUrl = queryParams.get('token');
    const loggedBy = queryParams.get('loggedBy');
    const careNavigatorId = queryParams.get('careNavigatorId');
    if (tokenFromUrl && loggedBy && careNavigatorId) {
      fetchPatientLogin();
      dispatch(setAuthToken(tokenFromUrl))
      localStorage.setItem("care_navigator", JSON.stringify(loggedBy === 'careNavigator' ? true : false))
      localStorage.setItem("careNavigatorId", JSON.stringify(careNavigatorId))
      dispatch(setIsCareNavigator(loggedBy === 'careNavigator' ? true : false))
      dispatch(setCareNavigatorId(Number(careNavigatorId)))
    }
  }, [])


  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path='/login' element={
            <AuthRoute>
              <Login />
            </AuthRoute>
          } />
          <Route path='/sso-login' element={
            <SsoLogin />
          } />
          <Route path='/signup' element={
            <AuthRoute>
              <Signup />
            </AuthRoute>
          } />
          <Route path='/forgot-password' element={
            <AuthRoute>
              <ForgotPassword />
            </AuthRoute>
          } />
          <Route path='/otp' element={
            <AuthRoute>
              <LoginOtp />
            </AuthRoute>
          } />
          <Route path='/change-password' element={
            <AuthRoute>
              <ChangePassword />
            </AuthRoute>
          } />
          <Route path='/pricing-policy' element={
            <AuthRoute>
              <PricingPolicy />
            </AuthRoute>
          } />
          <Route path='/terms' element={
            <AuthRoute>
              <TermsOfService />
            </AuthRoute>
          } />
          <Route path='/privacy-policy' element={
            <AuthRoute>
              <PrivacyPolicy />
            </AuthRoute>
          } />
          <Route path='/verify-email' element={
            <VerifyEmail />
          } />
          {
            (!isCareNavigator && token) &&
            <Route path='CallNow' element={<CallNow />} />
          }
          <Route path="/*"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
            <Route path='myaccount/profile' element={<Profile />} />
            <Route path='dashboard' element={<Dashboard />} />
            <Route path='CareTeam' element={<CareTeam />} />
            <Route path='CareTeam/Create' element={<CreateCareTeam />} />
            <Route path='MyAppointments' element={<MyAppointment />} />
            <Route path='MyAppointments/book' element={<BookAppointment />} />
            <Route path='MyAppointments/book/thanks' element={<BookAppointmentThanks />} />
            <Route path='SavedDocuments' element={<SavedDocument />} />
            <Route path='SavedDocuments/view' element={<SavedDocumentView />} />
            <Route path='help' element={<Help />} />
            <Route path='Assessments' element={<Assessments />} />
            <Route path='MedicalRecord' element={<MedicalRecord />} />
            <Route path='MyMedicine' element={<MyMedicines />} />
            <Route path='MyAppointments/details' element={<AppointmentDetails />} />
            <Route path='vital-monitoring' element={<RpmAlertManagement />} />
            <Route path='vital-monitoring/details' element={<RPMAlertManagementDetails />} />
            <Route path='vital-monitoring/add' element={<ManualVitalEntry />} />
            <Route path='BehaviouralHealth' element={<BehaviouralHealth />} />
            <Route path='LabTests' element={<LabTests />} />
            <Route path='device/omron' element={<OmronDevice />} />
            <Route path='myaccount/settings' element={<Setting />} />
            {
              !isCareNavigator &&
              <>
                <Route path='TalkToDoctor' element={<TalkToDoctor />} />
                {/* <Route path='CallNow' element={<CallNow />} /> */}
                <Route path='Assessments/test' element={<AssessmentTest />} />
                <Route path='BehaviouralHealth/test' element={<BehaviouralHealthDetails />} />
              </>
            }
            <Route path='LabTests/Book' element={<BookLabtest />} />
            <Route path='LabTests/Cart' element={<LabtestCart />} />
            <Route path='LabTests/Cart_Summary' element={<LabCartSummary />} />
            <Route path='LabTests/details' element={<LabTestDetails />} />
            <Route path='bookedLab/details' element={<BookedLabDetails />} />
            <Route path='careplan' element={<CarePlan />} />
            <Route path='inbox' element={<Chat />} />
            <Route path='Assessments/result' element={<AssessmentCheckResult />} />
            <Route path='Assessments/hra-result' element={<HraResult />} />
            <Route path='*' element={<Dashboard />} />
            <Route
              path="/*"
              element={<Navigate to={"/dashboard"} replace />}
            />
          </Route>
          <Route
            path="/*"
            element={<Navigate to={!token ? "/login" : ''} replace />}
          />
        </Routes>
      </Suspense>
    </>
  )
}

export default App